import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Close, Search } from "@similarweb/icons";
import { SegmentedControl } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex } from "@similarweb/ui-components/foundations";

import {
    EMPLOYEE_PROFILES_SEGMENTS,
    type EmployeeProfilesSegment,
} from "app/pages/sales-intelligence/modules/employee-profiles/types/employeeProfiles.types";

import { EmployeeProfilesNewButton } from "./EmployeeProfilesNewButton";

type EmployeeProfilesPageToolbarProps = {
    canCreateShared: boolean;
    sharedCount: number;
    privateCount: number;
    onSegmentChange: (segment: EmployeeProfilesSegment) => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onNewEmployeeProfile: (isSharedWithAccount: boolean) => void;
};

export const EmployeeProfilesPageToolbar = ({
    canCreateShared,
    sharedCount,
    privateCount,
    onSegmentChange,
    searchTerm,
    onSearchChange,
    onNewEmployeeProfile,
}: EmployeeProfilesPageToolbarProps) => {
    const { translate } = useTranslate();
    const [segmentIndex, setSegmentIndex] = useState(0);

    const segmentDisabled: Record<EmployeeProfilesSegment, boolean> = {
        all: sharedCount + privateCount === 0,
        shared: sharedCount === 0,
        private: privateCount === 0,
    };

    const segmentValues = EMPLOYEE_PROFILES_SEGMENTS.map((value) => ({
        value,
        label: translate(`si.employee-profiles.segment.${value}`),
        disabled: segmentDisabled[value],
    }));

    const handleSegmentChange = (index: number) => {
        setSegmentIndex(index);
        onSegmentChange(EMPLOYEE_PROFILES_SEGMENTS[index]);
    };

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
        >
            <Flex
                alignItems="center"
                gap="16px"
            >
                <SegmentedControl
                    values={segmentValues}
                    defaultSelectedIndex={segmentIndex}
                    onChange={handleSegmentChange}
                />
                <TextField
                    placeholder={translate("si.employee-profiles.search.placeholder")}
                    startAddon={<Search size="sm" />}
                    type="text"
                    onChange={onSearchChange}
                    value={searchTerm}
                    data-automation="employee-profiles-search"
                    endAddon={
                        searchTerm.length > 0 ? (
                            <Close
                                size="sm"
                                onClick={() => onSearchChange("")}
                                cursor="pointer"
                            />
                        ) : null
                    }
                />
            </Flex>
            <EmployeeProfilesNewButton
                canCreateShared={canCreateShared}
                onNewEmployeeProfile={onNewEmployeeProfile}
            />
        </Flex>
    );
};
